import { Menu, NewTooltip } from '@lib/components'
import type { ID } from '@octopus-do/octopus-project-core'
import { Badge, LoaderIcon } from '@octopus-do/octopus-storybook'
import {
  Code as CodeIcon,
  Content as ContentIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { useEffect, useRef } from 'react'

import { clsJoin } from '@/utils'
import { useSidebarMenu } from '../../useSidebarMenu'
import css from './styles.module.css'

interface SyncMenuItemProps {
  hasAccess: boolean
  canSync: boolean
  syncing: boolean
  onClick: () => void
  onBadgeClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

const SyncMenuItem: React.FC<SyncMenuItemProps> = ({
  hasAccess,
  canSync,
  syncing,
  onClick,
  onBadgeClick,
}) => (
  <Menu
    customClassName={clsJoin(
      (!hasAccess || !canSync || syncing) && css.menu_disabled
    )}
    // while the request runs there is nothing to click, so no hover either
    noHover={syncing}
    onClick={onClick}
  >
    <div
      className={clsJoin(
        css.seo_sidebar_menu,
        (!hasAccess || !canSync || syncing) && css.disabled
      )}
    >
      {syncing ? (
        <LoaderIcon className="svg-icon" />
      ) : (
        <RefreshIcon className="svg-icon" />
      )}
      <div className={css.seo_sidebar_menu_title}>Sync from URL</div>
      {!hasAccess && (
        <div className={css.seo_sidebar_menu_badge} onClick={onBadgeClick}>
          <Badge type="pro" size="large" />
        </div>
      )}
    </div>
  </Menu>
)

interface Props {
  nodeId: ID
  onCloseDropdown: () => void
}

export const RootMenu: React.FC<Props> = ({ nodeId, onCloseDropdown }) => {
  const {
    hasAccess,
    readOnly,
    canSync,
    syncing,
    syncHint,
    handleCopy,
    handleClickExport,
    handleClickPreview,
    handleClickSync,
    handleBadgeClick,
    handleMenuClick,
  } = useSidebarMenu(nodeId)

  const menuItemCls = clsJoin(css.seo_sidebar_menu, !hasAccess && css.disabled)

  const syncProps: SyncMenuItemProps = {
    hasAccess,
    canSync,
    syncing,
    onClick: handleClickSync,
    onBadgeClick: (e) => handleBadgeClick(e, onCloseDropdown),
  }

  // Меню держим открытым, пока пункт показывает лоадер, и закрываем, когда
  // запрос завершился — успехом или ошибкой.
  const wasSyncing = useRef(syncing)

  useEffect(() => {
    if (wasSyncing.current && !syncing) {
      onCloseDropdown()
    }

    wasSyncing.current = syncing
  }, [syncing, onCloseDropdown])

  // Подсказку про адрес показываем только тем, кому пункт доступен по плану:
  // у Free за серый цвет отвечает бейдж Pro.
  const showSyncHint = hasAccess && Boolean(syncHint)

  return (
    <>
      {!readOnly && (
        <>
          {showSyncHint ? (
            <NewTooltip
              content={syncHint}
              position="left"
              renderEl={(onSetRef, onMouseEnter, onMouseLeave) => (
                <div
                  ref={onSetRef}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                >
                  <SyncMenuItem {...syncProps} />
                </div>
              )}
            />
          ) : (
            <SyncMenuItem {...syncProps} />
          )}
          <div className={css.seo_sidebar_menu_divider} />
        </>
      )}

      <Menu
        onClick={() => {
          handleClickPreview()
          onCloseDropdown()
        }}
      >
        <div className={css.seo_sidebar_menu}>
          <ContentIcon className="svg-icon" />
          <div className={css.seo_sidebar_menu_title}>Preview</div>
        </div>
      </Menu>
      <Menu
        onClick={() => {
          handleCopy()
          onCloseDropdown()
        }}
      >
        <div className={css.seo_sidebar_menu}>
          <LinkIcon className="svg-icon" />
          <div className={css.seo_sidebar_menu_title}>Copy link</div>
        </div>
      </Menu>

      {!readOnly && (
        <>
          <div className={css.seo_sidebar_menu_divider} />

          <Menu
            customClassName={clsJoin(!hasAccess && css.menu_disabled)}
            onClick={() => handleMenuClick(handleClickExport, onCloseDropdown)}
          >
            <div className={menuItemCls}>
              <CodeIcon className="svg-icon" />
              <div className={css.seo_sidebar_menu_title}>Export code</div>
              {!hasAccess && (
                <div
                  className={css.seo_sidebar_menu_badge}
                  onClick={(e) => handleBadgeClick(e, onCloseDropdown)}
                >
                  <Badge type="pro" size="large" />
                </div>
              )}
            </div>
          </Menu>
        </>
      )}
    </>
  )
}
